import { formatLocation } from '@pickleballinc/lib/format-location';
import type { HpMarketingCardData } from '@pickleballinc/ui/types/hp-marketing-ad';

// Export names are inconsistent across these four files — two are suffixed `v2`, two are not — so the
// unsuffixed pair is aliased. Without that, `TournamentCard` / `ClubCard` here would read as the v1
// components of the same name in `@pickleballinc/ui/components/global/Cards`.
import { ClubCard as ClubCardv2 } from './ClubCardv2';
import { LeagueCardv2 } from './LeagueCardv2';
import { TeamLeagueCardv2 } from './TeamLeagueCardv2';
import { TournamentCard as TournamentCardv2 } from './TournamentCardv2';

/**
 * v2 (module card) renderer for HP marketing listing ads.
 *
 * Pass this to `<HpMarketingAd renderCard={...} />` behind the consuming app's
 * `FEATURE_FLAGS.ENABLE_NEW_CARDS`; omit the prop to keep the rotator's built-in v1 rendering.
 *
 * It lives here rather than in `@pickleballinc/ui` on purpose: the v2 cards are in this package, and
 * this package already depends on `ui`. Resolving the switch inside the rotator would have closed a
 * `ui -> features -> ui` cycle, so the rotator exposes an injection point instead and the app decides.
 *
 * Content parity with the v1 rendering is deliberate:
 *   - `registrationCount: 0` with no `status` leaves `showFooterRow` false, which is the v2 equivalent
 *     of v1's `hideBottomRow` — the slim ad payload carries no counts or price to put there.
 *   - `hideRegisteredPlayers` suppresses the players line for the same reason.
 *   - `showFavoriteIcon` is never set. An advertisement is not the viewer's listing to favourite, and
 *     the favourite state needs app-specific hooks the rotator has no access to.
 *   - The link always comes from `customCardHref`; the `*AppBaseURL` props are only the cards'
 *     fallback href builders and are optional on the ad payload, so they default to ''.
 *   - `nativeAnchor` on every card: an ad click must be a full document navigation, not a Next
 *     client-side transition. The ad URL is a same-origin URL carrying Everflow parameters
 *     (`oid` / `affid`), and the Everflow landing middleware only records the click, sets the cookies
 *     and cleans the URL on document requests — a `<Link>` would skip all of that and leave the
 *     parameters in the address bar.
 *
 * Sizing is left to `ModuleCard`, which is already responsive (`w-full min-w-0 max-w-[343px]`, then
 * `min-w-[224px] max-w-[286px]` from 500px up) — the same tokens the rotator's grid cell expects.
 * Do not add a fixed px width here; a definite width inside the rotator's shrink-wrapping grid cell
 * overflows mobile viewports.
 */
export function renderHpMarketingCardV2(card: HpMarketingCardData): React.ReactNode {
	switch (card.kind) {
		case 'tournament':
			return (
				<TournamentCardv2
					id={card.id}
					slug={card.slug}
					title={card.title}
					logo={card.logo}
					location={card.location}
					dateFrom={card.dateFrom}
					dateTo={card.dateTo}
					currency=""
					price={0}
					registrationCount={0}
					isRegistrationClosed={false}
					hideRegisteredPlayers
					tournamentsAppBaseURL={card.tournamentsAppBaseURL}
					customCardHref={card.customCardHref}
					nativeAnchor
					isTournamentsApp
				/>
			);
		case 'league':
			return (
				<LeagueCardv2
					id={card.id}
					slug={card.slug}
					title={card.title}
					clubTitle={card.clubTitle}
					logo={card.logo}
					location={card.hideLocation ? '' : card.location}
					dateFrom={card.dateFrom}
					dateTo={card.dateTo}
					currency=""
					formatTag=""
					price="0"
					registrationCount={0}
					hideRegisteredPlayers
					shouldHideStartTime
					leaguesAppBaseURL={card.leaguesAppBaseURL ?? ''}
					customCardHref={card.customCardHref}
					openInNewTab={card.openInNewTab}
					nativeAnchor
				/>
			);
		case 'org':
			return (
				<ClubCardv2
					id={card.id}
					slug={card.slug}
					title={card.title}
					logo={card.logo}
					location={card.location}
					memberCount={card.memberCount}
					clubsAppBaseURL={card.clubsAppBaseURL ?? ''}
					customCardHref={card.customCardHref}
					nativeAnchor
				/>
			);
		case 'team-league':
			// The team-league payload is the one that isn't already flat: `logo` is `{ uuid, url }` and
			// `location` is a nested city/state/country object, because v1's `TeamLeagueCard` took a whole
			// `league` literal. v2 takes plain strings, so both are flattened here.
			return (
				<TeamLeagueCardv2
					id={card.id}
					slug={card.slug}
					title={card.title}
					logo={card.logo?.url ?? ''}
					location={formatLocation([card.location?.city, card.location?.stateInfo?.abbreviation, card.location?.countryInfo?.abbreviation])}
					startDatetime={card.dateFrom}
					endDatetime={card.dateTo}
					playerCount={0}
					hidePlayersCount
					teamLeaguesBaseUrl={card.teamLeaguesBaseUrl}
					customCardHref={card.customCardHref}
					nativeAnchor
				/>
			);
		default: {
			const exhaustive: never = card;
			throw new Error(`Unknown listing-ad card kind: ${(exhaustive as { kind: string }).kind}`);
		}
	}
}
